import {
  CrmFieldService,
  StandardIds,
  SystemFieldIds,
} from '@galaxy/crm/services/field-service';
import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  Input,
  Signal,
  signal,
  WritableSignal,
  ChangeDetectionStrategy
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { TranslationModule } from '../../../i18n/translation-module';
import { CollapsibleSectionComponent } from '../../collapsible-section/collapsible-section.component';
import { ActivityInterface } from '@vendasta/crm';
import { intervalToDuration } from 'date-fns';
import { DateFormat } from '@vendasta/galaxy/utility/date-utils';
import { ActivityRichTextComponent } from './activity-rich-text.component';

@Component({
  selector: 'crm-activity-logged-meeting',
  templateUrl: './logged-meeting.component.html',
  styleUrls: ['./logged-meeting.component.scss'],
  providers: [],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CommonModule, TranslateModule, TranslationModule, CollapsibleSectionComponent, ActivityRichTextComponent],
})
export class CrmActivityLoggedMeetingComponent {
  dateFormat = DateFormat.mediumDateShortTime;
  private _activity: WritableSignal<ActivityInterface> = signal({});
  @Input({ required: true }) set activity(activity: ActivityInterface) {
    this._activity.set(activity);
  }
  get activity() {
    return this._activity();
  }

  cardInfo: Signal<ActivityCardInformation> = computed(() => {
    const duration = this.getIntegerFieldData(this._activity(), StandardIds.ActivityMeetingDuration);
    return {
      note: this.getStringFieldData(this._activity(), StandardIds.ActivityMeetingBody),
      status: this.getStringFieldData(this._activity(), StandardIds.ActivityMeetingStatus),
      outcome: this.getStringFieldData(this._activity(), StandardIds.ActivityMeetingOutcome),
      date:
        this.fieldService.getFieldValueFromCrmObject(this._activity(), SystemFieldIds.ActivityEffectiveDate)
          ?.dateValue ?? null,
      duration: this.convertMillisecondsToHoursAndMinutes(duration),
    };
  });

  private getStringFieldData(activity: ActivityInterface, fieldId: string): string {
    return this.fieldService.getFieldValueFromCrmObject(activity, fieldId)?.stringValue ?? '';
  }
  private getIntegerFieldData(activity: ActivityInterface, fieldId: string): number {
    return this.fieldService.getFieldValueFromCrmObject(activity, fieldId)?.integerValue ?? 0;
  }
  private convertMillisecondsToHoursAndMinutes(duration: number): string {
    if (duration === 0) return '';
    const convertedDuration = intervalToDuration({ start: 0, end: duration });
    if ((convertedDuration.hours ?? 0) > 0) {
      return `${convertedDuration.hours}h ${convertedDuration.minutes}m`;
    }
    return `${convertedDuration.minutes}m`;
  }

  constructor(private readonly fieldService: CrmFieldService) {}
}

interface ActivityCardInformation {
  note: string;
  outcome: string;
  status: string;
  date: Date | null;
  duration: string;
}
